import { Divider, MenuItem, Select, Slider, Typography } from "@mui/material";
import { useEffect } from "react";
import Grid from "@mui/material/Grid2";
import Help from "../common/Help";
import VisitTypeDuration from "../VisitTypeDuration";
import StartEndTime from "../common/forms/StartEndTime";
import FilterGridRow from "../scheduler/FilterGridRow";
import type { CareRequirement } from "@caredna-marlin/utils/client";

type Props = {
  careRequirement: CareRequirement
  serviceLineId: number
  setCareRequirement: (careReq: CareRequirement) => void
  timezone?: string
  isFirstVisitOfDay: boolean
  disallowRelativeSchedule?: boolean
}

const CareRequirementFormRow = ({ careRequirement, serviceLineId, setCareRequirement, timezone, isFirstVisitOfDay, disallowRelativeSchedule = false }: Props ) => {
  const scheduleType = careRequirement.scheduleType;

  // If this row isn't allowed to use "Relative to last visit" but its stored
  // scheduleType is exactly that, coerce it back to a time range on mount so
  // the form is in a valid state.
  useEffect(() => {
    if (disallowRelativeSchedule && scheduleType === "previousVisitRelative") {
      setCareRequirement({ ...careRequirement, scheduleType: "timeRange" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disallowRelativeSchedule]);
  const afterLastVisitLabel = isFirstVisitOfDay
    ? "After last visit (on previous day)"
    : "After last visit";
  const minAfterLastVisit = Math.min(12, Math.max(0, careRequirement.minHoursAfterLastVisit ?? 0));
  const maxAfterLastVisit = Math.min(12, Math.max(0, careRequirement.maxHoursAfterLastVisit ?? 12));
  const [rangeMin, rangeMax] = minAfterLastVisit <= maxAfterLastVisit
    ? [minAfterLastVisit, maxAfterLastVisit]
    : [maxAfterLastVisit, minAfterLastVisit];

  return <Grid container columnSpacing={5} rowSpacing={1}>
    <Grid size={12}>
      <Divider sx={{ mb: 1 }} />
    </Grid>

    <Grid size={{ xl: 6, lg: 12 }}>
      <Grid container spacing={3} alignItems="center">
        <FilterGridRow label="Schedule">
          <Select
            aria-label="Schedule"
            value={scheduleType}
            onChange={(e) => setCareRequirement({ ...careRequirement, scheduleType: e.target.value as CareRequirement["scheduleType"] })}
            fullWidth
            size="small"
          >
            <MenuItem value="timeRange">Time range</MenuItem>
            {(!disallowRelativeSchedule || scheduleType === "previousVisitRelative") && (
              <MenuItem value="previousVisitRelative" disabled={disallowRelativeSchedule}>
                Relative to last visit
              </MenuItem>
            )}
          </Select>
        </FilterGridRow>
        {scheduleType === "timeRange" && <>
          <FilterGridRow label="Time">
            <StartEndTime
              startTime={careRequirement.minTime}
              endTime={careRequirement.maxTime}
              timezone={timezone}
              mode="time"
              onChange={(minTime, maxTime) => setCareRequirement({ ...careRequirement, minTime, maxTime })}
            />
          </FilterGridRow>
        </>}
        {scheduleType === "previousVisitRelative" && <>
          <FilterGridRow
            label={afterLastVisitLabel}
            helpText={"How far should the start time of this visit be from the start time of the last visit?"}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 8 }}>
                <Slider
                  value={[rangeMin, rangeMax]}
                  min={0}
                  max={12}
                  step={1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 1, label: '1' },
                    { value: 2, label: '2' },
                    { value: 3, label: '3' },
                    { value: 4, label: '4' },
                    { value: 5, label: '5' },
                    { value: 6, label: '6' },
                    { value: 7, label: '7' },
                    { value: 8, label: '8' },
                    { value: 9, label: '9' },
                    { value: 10, label: '10' },
                    { value: 11, label: '11' },
                    { value: 12, label: '12' }
                  ]}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => {
                    if (!Array.isArray(value)) { return }
                    setCareRequirement({
                      ...careRequirement,
                      minHoursAfterLastVisit: value[0],
                      maxHoursAfterLastVisit: value[1],
                    })
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2">
                  {rangeMin}–{rangeMax} hours after last visit
                </Typography>
              </Grid>
            </Grid>
          </FilterGridRow>
        </>}
      </Grid>
    </Grid>

    <Grid size={{ xl: 6, lg: 12 }}>
      <Grid container rowSpacing={3} alignItems="center">
        <VisitTypeDuration
          values={careRequirement}
          setValues={(values) => setCareRequirement({ ...careRequirement, ...values })}
          serviceLineId={serviceLineId.toString()}
        />
      </Grid>
    </Grid>
  </Grid>
}

export default CareRequirementFormRow
